import moment from 'moment';
import type { CoreTypes } from '../../core';
import type { Types as VKlassTypes } from '../types';
import type { InternalIdKey } from '../../core/types/common';
import { range } from '../../common/functions';

export namespace OutTypes {
  export type teacher  = Omit<CoreTypes.Serialized.Teacher, InternalIdKey>;
  export type group    = Omit<CoreTypes.Serialized.Group,   InternalIdKey>;
  export type course   = Omit<CoreTypes.Serialized.Course,  InternalIdKey>;
}

type Out = {
  teachers: OutTypes.teacher[];
  courses:  OutTypes.course [];
  groups:   OutTypes.group  [];
  meta: {
    errors?:   string[];
    warnings?: string[];
  };
};


export default function (
  src:     VKlassTypes.schedule,
  options: VKlassTypes.Options
): Out {

  // gather all import related warnings/errors
  const warnings: string[] = [];
  const errors:   string[] = [];

  // the schedule period range
  const scheduleRange = {
    start: moment.utc(options.beg),
    end:   moment.utc(options.end)
  };

  ////
  //// teachers
  ////
  const teachers: OutTypes.teacher[] = [];
  src.staff.forEach(x => {
    teachers.push({
      ids:         x.ID.toString(),
      displayName: x.Firstname + ' ' + x.Lastname
    });
  });


  ////
  //// groups
  ////
  const groups: OutTypes.group[] = [];
  src.classes.forEach(x => {
    groups.push({
      ids:         x.ID.toString(),
      displayName: x.Name,
      species:     'class',
    });
  });


  // build group to staff map
  const groupStaffMap = new Map<number, Set<number> >();
  src.groups_staff.forEach(x => {
    if(!groupStaffMap.has(x.groupId)) groupStaffMap.set(x.groupId, new Set());
    groupStaffMap.get(x.groupId)?.add(x.userId);
  });

  // build student to class map
  const studentClassMap = new Map<number, Set<number> >();
  src.classes_students.forEach(x => {
    if(!studentClassMap.has(x.userId)) studentClassMap.set(x.userId, new Set());
    studentClassMap.get(x.userId)?.add(x.classId);
  });

  // build group to classes map
  const groupClassesMap = new Map<number, Set<number> >();
  src.groups_students.forEach(x => {
    if (!groupClassesMap.has(x.groupId)) groupClassesMap.set(x.groupId, new Set());
    studentClassMap.get(x.userId)?.forEach(y => groupClassesMap.get(x.groupId)?.add(y));
  });


  ////
  //// courses
  ////
  const courses: OutTypes.course[] = [];
  src.groups.forEach(x => {
    // ignore courses who's period range does not overlap with the schedule period range
    const courseRange = {
      start: moment.utc(x.StartDate),
      end:   moment.utc(x.EndDate)
    };
    if (!range.overlaps(scheduleRange, courseRange)) return;

    const teachers = [...groupStaffMap.get(x.ID)   ?? []].map(x => ({ to: x.toString() }));
    const groups   = [...groupClassesMap.get(x.ID) ?? []].map(x => ({ to: x.toString() }));

    courses.push({
      ids:         x.ID.toString(),
      displayName: x.Name || x.FriendlyName,
      ...(teachers && { teachers }),
      ...(groups   && { groups }),
    });
  });



  return { teachers, groups, courses,
    meta: {
      ...( errors.length   ? { errors:   errors   } : { } ),
      ...( warnings.length ? { warnings: warnings } : { } )
    }
  };
}
